import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { QrCode } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [step, setStep] = useState(1);
  const [totpSecret, setTotpSecret] = useState("");
  const [totpQrUri, setTotpQrUri] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        role
      });

      setTotpSecret(response.data.totp_secret);
      setTotpQrUri(response.data.totp_qr_uri);
      setStep(2);
      toast.success("Account created! Please set up 2FA");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/verify-totp`, null, {
        params: { email, totp_code: totpCode }
      });

      toast.success("2FA enabled successfully!");
      
      // Auto login
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email,
        password,
        totp_code: totpCode
      });

      onLogin(loginResponse.data.user, loginResponse.data.access_token);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a202c' }}>SaakhSetu</CardTitle>
          <CardDescription className="text-center">
            {step === 1 ? "Create your account" : "Set up Two-Factor Authentication"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  placeholder="agent@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="register-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={setRole} required>
                  <SelectTrigger data-testid="register-role-select" className="relative z-10">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="System Administrator">System Administrator</SelectItem>
                    <SelectItem value="Credit Officer">Credit Officer</SelectItem>
                    <SelectItem value="Field Agent">Field Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !role}
                data-testid="register-submit-button"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 18, height: 18 }} />
                    Creating Account...
                  </span>
                ) : "Continue"}
              </Button>

              <div className="text-center text-sm">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold" style={{ color: '#667eea' }} data-testid="login-link">
                  Login here
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyTotp} className="space-y-4">
              <Alert>
                <QrCode className="h-4 w-4" />
                <AlertDescription>
                  Scan this QR code with Google Authenticator or any TOTP app
                </AlertDescription>
              </Alert>

              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpQrUri)}`}
                  alt="TOTP QR Code"
                  data-testid="totp-qr-code"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-center block">Secret Key (Manual Entry)</Label>
                <Input
                  value={totpSecret}
                  readOnly
                  className="text-center font-mono"
                  data-testid="totp-secret-display"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-center block">Enter 6-Digit Code</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={totpCode}
                    onChange={(value) => setTotpCode(value)}
                    data-testid="register-totp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || totpCode.length !== 6}
                data-testid="verify-totp-button"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? "Verifying..." : "Verify & Complete Setup"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;